import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Col, Container, Row, Form, Button, Alert, ProgressBar, Badge } from "react-bootstrap";
import UploadIcon from "../assets/images/upload_file_rgb_red.svg";
import "../assets/styles/dataImport.scss";
import api from "../services/httpClient";

const DataImportUpload = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [executionDate, setExecutionDate] = useState("");
  const [rollbackDate, setRollbackDate] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [showNoChangesConfirm, setShowNoChangesConfirm] = useState(false);
  const [clearConfirmed, setClearConfirmed] = useState(false);

  const minDate = new Date().toISOString().slice(0, 16);
  const importTimeout = 50 * 60 * 1000; // 50 minut dla dużych plików CSV

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && !selectedFile.name.endsWith(".csv")) {
      setError("Dopuszczalny format pliku to CSV.");
      setFile(null);
      return;
    }
    setError(null);
    setFile(selectedFile);
    setValidationResult(null);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setClearConfirmed(false);

    if (!file) {
      setError("Proszę wybrać plik CSV.");
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);
      if (executionDate) formData.append("executionDate", executionDate);
      if (rollbackDate) formData.append("rollbackDate", rollbackDate);

      // Etap 1: Upload + walidacja
      const response = await api.post(`/Import/Upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: importTimeout
      });

      const result = response.data;

      if (result.errors && result.errors.length > 0) {
        // Wyświetl błędy walidacji
        setValidationResult({ type: "errors", data: result });
      } else if (result.changes) {
        // Wyświetl podgląd zmian — oczekuje na potwierdzenie
        setValidationResult({ type: "preview", data: result });
      } else {
        // Sukces bezpośredni
        setSuccess(result.message || "Plik został przekazany do importu.");
        setTimeout(() => navigate("/data-import"), 2000);
      }
    } catch (err) {
      const msg = err.response?.data?.message || "Wystąpił błąd podczas uploadu pliku.";
      setError(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleConfirm = async () => {
    if (!validationResult?.data?.fileName) return;

    // Jeśli brak zmian — pytaj użytkownika o potwierdzenie
    if (validationResult.data.hasChanges === false && !showNoChangesConfirm) {
      setShowNoChangesConfirm(true);
      return;
    }

    setShowNoChangesConfirm(false);
    setUploading(true);
    setError(null);

    try {
      const response = await api.post(`/Import/Confirm`, {
        fileName: validationResult.data.fileName,
        executionDate: executionDate || null,
        rollbackDate: rollbackDate || null,
        changesSummary: validationResult.data.changesSummary || null,
        totalChanges: validationResult.data.totalChanges || null,
        changedFields: validationResult.data.changedFields || null,
        valueCounts: validationResult.data.valueCounts || null
      },
      {
        timeout: importTimeout
      }
    );

      const msg = response.data?.message || "Import został przekazany do przetwarzania.";
      setSuccess(msg);
      setValidationResult(null);
      setTimeout(() => navigate("/data-import"), 2000);
    } catch (err) {
      const msg = err.response?.data?.message || "Wystąpił błąd podczas zatwierdzania importu.";
      setError(msg);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="data-import-container">
      <Container fluid>
        <Row className="align-items-center mb-4">
          <Col xs="auto">
            <img src={UploadIcon} alt="Data Import Icon" style={{ width: "30px", height: "30px" }} />
          </Col>
          <Col>
            <h3 style={{ fontSize: "1.5rem", fontWeight: "bold" }}>
              Importuj plik CSV
            </h3>
          </Col>
          <Col xs="auto">
            <Button variant="secondary" onClick={() => navigate("/data-import")}>
              Powrót do listy
            </Button>
          </Col>
        </Row>

        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        {/* Formularz uploadu */}
        {!validationResult && (
          <Row>
            <Col md={6}>
              <Form onSubmit={handleUpload}>
                <Form.Group className="mb-3" controlId="csvFile">
                  <Form.Label>Plik CSV</Form.Label>
                  <Form.Control
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    disabled={uploading}
                  />
                  <Form.Text className="text-muted">
                    Wymagana kolumna „ID" oraz dowolna liczba kolumn, które mają zostać przekazane do aktualizacji.
                    <br/>Puste dane w wierszach dla konkretnej kolumny oznaczają brak zmiany (dlatego podana w excelu ilość kolumn może być mniejsza). 
                    Jeżeli jednak Twoją intencją jest wyczyścić wartość dla danej pozycji w poszczególnej kolumnie i naturalnie myślisz nad wstawieniem pustej wartości, to użyj wartości „[CLEAR]" aby wyczyścić/wyzerować konkretne pole.
                    Inaczej system zinterpretuje pustą wartość jako brak zmiany dla tego pola. Typ pliku to CSV rozdzielone średnikami „;".
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3" controlId="executionDate">
                  <Form.Label>Data wykonania (opcjonalna)</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={executionDate}
                    min={minDate}
                    onChange={(e) => setExecutionDate(e.target.value)}
                    disabled={uploading}
                  />
                  <Form.Text className="text-muted">
                    Jeśli podana — import zostanie zaplanowany na wskazaną datę.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3" controlId="rollbackDate">
                  <Form.Label>Data wycofania (opcjonalna)</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={rollbackDate}
                    min={minDate}
                    onChange={(e) => setRollbackDate(e.target.value)}
                    disabled={uploading}
                  />
                  <Form.Text className="text-muted">
                    Data automatycznego wycofania zmian. Musi być późniejsza niż data wykonania.
                  </Form.Text>
                </Form.Group>

                {uploading && <ProgressBar animated now={100} className="mb-3" label="Przetwarzanie..." />}

                <div className="d-flex gap-2">
                  <Button variant="primary" type="submit" disabled={uploading || !file}>
                    {uploading ? "Wysyłanie..." : "Wyślij i waliduj"}
                  </Button>
                  <Button variant="outline-secondary" onClick={() => navigate("/data-import")} disabled={uploading}>
                    Anuluj
                  </Button>
                </div>
              </Form>
            </Col>
          </Row>
        )}

        {/* Podgląd zmian przed zatwierdzeniem */}
        {validationResult?.type === "preview" && (
          <Row>
            <Col>
              <Alert variant="info">
                <strong>Podgląd zmian</strong> — walidacja pliku zakończona pomyślnie.
                <br />
                Łączna liczba rekordów w pliku: {validationResult.data.totalRecords || "N/A"}
                <br />
                Rekordów ze zmianami: {validationResult.data.totalChanges || 0}
                <br />
                Łączna liczba zmienionych pól: {validationResult.data.changedFields || 0}
                {validationResult.data.changesSummary && Object.keys(validationResult.data.changesSummary).length > 0 && (
                  <ul className="mt-2 mb-0">
                    {Object.entries(validationResult.data.changesSummary).map(([field, count]) => {
                      // Rozkład nowych wartości dla kolumny (może nie istnieć — starsze odpowiedzi API)
                      const dist = validationResult.data.valueCounts?.[field];
                      return (
                        <li key={field}>
                          <strong>{field}</strong>: {count} zmian
                          {dist?.topValues && Object.keys(dist.topValues).length > 0 && (
                            <ul className="mb-0">
                              {Object.entries(dist.topValues).map(([val, cnt]) => (
                                <li key={val}>
                                  <em>{val === "" ? "(wyczyszczenie)" : val}</em> — {cnt}
                                </li>
                              ))}
                              {dist.otherValuesCount > 0 && (
                                <li className="text-muted">
                                  + {dist.otherValuesCount} innych wartości ({dist.otherChangesCount} zmian)
                                </li>
                              )}
                            </ul>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </Alert>

              {/* Informacja o kolumnach: edytowalne vs tylko do odczytu */}
              {(validationResult.data.editableColumns?.length > 0 || validationResult.data.readOnlyColumns?.length > 0) && (
                <Row className="mb-3">
                  {validationResult.data.editableColumns?.length > 0 && (
                    <Col md={6}>
                      <Alert variant="success" className="py-2 mb-0">
                        <small><strong>Kolumny edytowalne ({validationResult.data.editableColumns.length}):</strong></small>
                        <br />
                        <small>{validationResult.data.editableColumns.join(", ")}</small>
                      </Alert>
                    </Col>
                  )}
                  {validationResult.data.readOnlyColumns?.length > 0 && (
                    <Col md={6}>
                      <Alert variant="secondary" className="py-2 mb-0">
                        <small><strong>Kolumny tylko do odczytu ({validationResult.data.readOnlyColumns.length}):</strong></small>
                        <br />
                        <small>{validationResult.data.readOnlyColumns.join(", ")}</small>
                      </Alert>
                    </Col>
                  )}
                </Row>
              )}

              {/* Tabela diff — szczegóły zmian per rekord */}
              {validationResult.data.changes && validationResult.data.changes.length > 0 && (
                <div style={{ maxHeight: "500px", overflowY: "auto" }} className="mb-3">
                  <table className="table table-bordered table-sm table-striped">
                    <thead className="table-dark" style={{ position: "sticky", top: 0 }}>
                      <tr>
                        <th style={{ width: "80px" }}>ID</th>
                        <th style={{ width: "90px" }}>Kod</th>
                        <th style={{ width: "150px" }}>Miejscowość</th>
                        <th style={{ width: "200px" }}>Adres</th>
                        <th>Zmiany</th>
                      </tr>
                    </thead>
                    <tbody>
                      {validationResult.data.changes.map((change, idx) => (
                        <tr key={idx}>
                          <td>{change.id}</td>
                          <td>{change.postcode}</td>
                          <td>{change.cityName}</td>
                          <td>{change.address}</td>
                          <td dangerouslySetInnerHTML={{ __html: change.changes }} />
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Sekcja czyszczenia wartości — wymaga potwierdzenia przez użytkownika */}
              {validationResult.data.requiresConfirmation && (
                <Alert variant="warning" className="mb-3">
                  <Alert.Heading>
                    <span role="img" aria-label="warning">⚠️</span> Wykryto czyszczenie wartości w kolumnach krytycznych
                  </Alert.Heading>
                  <p>
                    Poniższe zmiany oznaczają usunięcie wartości z kolumn operacyjnych (trasy, serwisy, strefy).
                    Upewnij się, że jest to zamierzone działanie.
                  </p>
                  <p className="text-muted" style={{ fontSize: "0.85rem" }}>
                    Jeśli czyszczenie wartości nie jest Twoją intencją, a system nieprawidłowo interpretuje dane —
                    zgłoś problem do <strong>ITDHL</strong>.
                  </p>
                  <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                    <table className="table table-bordered table-sm mb-0">
                      <thead className="table-warning" style={{ position: "sticky", top: 0 }}>
                        <tr>
                          <th style={{ width: "80px" }}>ID</th>
                          <th style={{ width: "90px" }}>Kod</th>
                          <th style={{ width: "150px" }}>Miejscowość</th>
                          <th>Kolumny do wyczyszczenia</th>
                        </tr>
                      </thead>
                      <tbody>
                        {validationResult.data.changes
                          .filter(c => c.requiresConfirmation)
                          .map((change, idx) => (
                            <tr key={idx}>
                              <td>{change.id}</td>
                              <td>{change.postcode}</td>
                              <td>{change.cityName}</td>
                              <td dangerouslySetInnerHTML={{ __html: change.changes }} />
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                  <Form.Check
                    type="checkbox"
                    id="clearConfirmCheckbox"
                    label="Potwierdzam, że chcę wyczyścić powyższe wartości w kolumnach krytycznych"
                    checked={clearConfirmed}
                    onChange={(e) => setClearConfirmed(e.target.checked)}
                    className="mt-3 fw-bold"
                  />
                </Alert>
              )}

              {validationResult.data.hasClearActions && !validationResult.data.requiresConfirmation && (
                <Alert variant="info" className="mb-3">
                  <span role="img" aria-label="info">ℹ️</span>{" "}
                  Import zawiera akcje czyszczenia wartości (marker [CLEAR]). Szczegóły widoczne w tabeli zmian powyżej.
                  <p className="text-muted mt-1 mb-0" style={{ fontSize: "0.85rem" }}>
                    Jeśli czyszczenie wartości nie jest Twoją intencją — zgłoś problem do <strong>ITDHL</strong>.
                  </p>
                </Alert>
              )}

              {showNoChangesConfirm && (
                <Alert variant="warning" className="mb-3">
                  <strong>Wykryto brak zmian</strong> pomiędzy aktualnym stanem bazy a importowanym plikiem.
                  <br />Czy mimo to chcesz kontynuować import danych?
                  <div className="d-flex gap-2 mt-2">
                    <Button variant="warning" size="sm" onClick={handleConfirm}>
                      Tak, kontynuuj import
                    </Button>
                    <Button variant="outline-secondary" size="sm" onClick={() => setShowNoChangesConfirm(false)}>
                      Anuluj
                    </Button>
                  </div>
                </Alert>
              )}

              <div className="d-flex gap-2">
                <Button variant="success" onClick={handleConfirm} disabled={uploading || (validationResult?.data?.requiresConfirmation && !clearConfirmed)}>
                  {uploading ? "Zatwierdzanie..." : "Aktualizuj (zatwierdź import)"}
                </Button>
                <Button variant="outline-secondary" onClick={() => { setValidationResult(null); setShowNoChangesConfirm(false); }}>
                  Powrót do formularza
                </Button>
              </div>
            </Col>
          </Row>
        )}

        {/* Błędy walidacji */}
        {validationResult?.type === "errors" && (
          <Row>
            <Col>
              <Alert variant="danger" className="mb-3">
                <Alert.Heading>
                  <span role="img" aria-label="error">⚠️</span> Walidacja pliku wykazała błędy
                </Alert.Heading>
                <p className="mb-0">
                  Poniżej znajduje się lista błędów wykrytych w pliku CSV. <br/>Popraw dane w pliku i spróbuj ponownie.
                </p>
              </Alert>

              {/* Błędy jednorodności kodów pocztowych */}
              {validationResult.data.postcodeErrors && validationResult.data.postcodeErrors.length > 0 && (
                <div className="mb-4">
                  <h6 className="text-danger mb-2">
                    Błędy jednorodności kodów pocztowych ({validationResult.data.postcodeErrors.length})
                  </h6>
                  <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                    <table className="table table-bordered table-sm table-striped mb-0">
                      <thead className="table-danger" style={{ position: "sticky", top: 0 }}>
                        <tr>
                          <th style={{ width: "120px" }}>Kod pocztowy</th>
                          <th style={{ width: "150px" }}>Pole</th>
                          <th>Znalezione wartości</th>
                        </tr>
                      </thead>
                      <tbody>
                        {validationResult.data.postcodeErrors.map((err, idx) => (
                          <tr key={idx}>
                            <td><code>{err.postcode}</code></td>
                            <td><strong>{err.field}</strong></td>
                            <td>{err.values?.join(", ")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Błędy walidacji wierszy */}
              {validationResult.data.errors && validationResult.data.errors.length > 0 && (
                <div className="mb-4">
                  <h6 className="text-danger mb-2">
                    Błędy walidacji danych ({validationResult.data.errors.length} {validationResult.data.errors.length === 1 ? "rekord" : validationResult.data.errors.length < 5 ? "rekordy" : "rekordów"})
                  </h6>
                  <div style={{ maxHeight: "500px", overflowY: "auto" }}>
                    {validationResult.data.errors.map((err, idx) => (
                      <div key={idx} className="card mb-2 border-danger">
                        <div className="card-header bg-danger bg-opacity-10 py-2 d-flex justify-content-between align-items-center">
                          <span>
                            <strong>ID rekordu: </strong>
                            <code>{err.recordId}</code>
                          </span>
                          <Badge bg="danger" pill>{err.errorCount} {err.errorCount === 1 ? "błąd" : err.errorCount < 5 ? "błędy" : "błędów"}</Badge>
                        </div>
                        <ul className="list-group list-group-flush">
                          {err.messages?.map((msg, msgIdx) => (
                            <li key={msgIdx} className="list-group-item list-group-item-light py-1 px-3" style={{ fontSize: "0.9rem" }}>
                              {msg}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="d-flex gap-2 mt-3">
                <Button variant="outline-secondary" onClick={() => setValidationResult(null)}>
                  Powrót do formularza
                </Button>
              </div>
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
};

export default DataImportUpload;
